/**
 * Distillery service for CRUD operations
 */

import { supabase } from './supabase';

/**
 * Distillery service object with all database operations
 */
export const distilleryService = {
  /**
   * Fetches distilleries with pagination, sorting, and filtering
   * @param {Object} options - Query options
   * @param {number} options.page - Current page number
   * @param {number|string} options.limit - Items per page or 'All'
   * @param {string} options.sortBy - Column to sort by
   * @param {string} options.sortOrder - Sort order (asc/desc)
   * @param {Object} options.filters - Filter criteria
   * @returns {Promise<{data: Array, count: number}>} Distilleries and total count
   */
  async getDistilleries({ page = 1, limit = 20, sortBy = 'distillery_name', sortOrder = 'asc', filters = {} }) {
    const { data: { user } } = await supabase.auth.getUser();

    let query = supabase
      .from('distilleries')
      .select('*', { count: 'exact' })
      .eq('user_id', user.id)
      .order(sortBy, { ascending: sortOrder === 'asc' });

    if (filters.search) {
      query = query.or(`distillery_name.ilike.%${filters.search}%,city.ilike.%${filters.search}%,state_region.ilike.%${filters.search}%`);
    }

    if (filters.country) {
      query = query.eq('country', filters.country);
    }

    if (limit !== 'All') {
      const from = (page - 1) * limit;
      const to = from + limit - 1;
      query = query.range(from, to);
    }

    const { data, error, count } = await query;

    if (error) throw error;
    return { data: data || [], count: count || 0 };
  },

  /**
   * Finds the current user's distillery by name (case-insensitive)
   * @param {string} name - Distillery name to search
   * @returns {Promise<Object|null>} Matching distillery or null
   */
  async findByName(name) {
    if (!name) return null;
    const { data: { user } } = await supabase.auth.getUser();
    const { data } = await supabase
      .from('distilleries')
      .select('id, distillery_name, country')
      .eq('user_id', user.id)
      .ilike('distillery_name', name)
      .maybeSingle();
    return data || null;
  },

  /**
   * Fetches all distilleries for dropdown select
   * @returns {Promise<Array>} All distilleries sorted by name
   */
  async getAllDistilleries() {
    const { data, error } = await supabase
      .from('distilleries')
      .select('id, distillery_name, country')
      .order('distillery_name', { ascending: true });

    if (error) throw error;
    return data || [];
  },

  /**
   * Fetches a single distillery by ID
   * @param {string} id - Distillery UUID
   * @returns {Promise<Object>} Distillery data
   */
  async getDistilleryById(id) {
    const { data, error } = await supabase
      .from('distilleries')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw error;
    return data;
  },

  /**
   * Creates a new distillery
   * @param {Object} distillery - Distillery data
   * @returns {Promise<Object>} Created distillery
   */
  async createDistillery(distillery) {
    const { data: { user } } = await supabase.auth.getUser();

    const { data, error } = await supabase
      .from('distilleries')
      .insert({
        ...distillery,
        user_id: user.id,
        created_by: user.id
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  /**
   * Updates an existing distillery
   * @param {string} id - Distillery UUID
   * @param {Object} updates - Fields to update
   * @returns {Promise<Object>} Updated distillery
   */
  async updateDistillery(id, updates) {
    const { data: { user } } = await supabase.auth.getUser();

    const { data, error } = await supabase
      .from('distilleries')
      .update({
        ...updates,
        updated_by: user.id
      })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  /**
   * Deletes a distillery if it has no associated bottles
   * @param {string} id - Distillery UUID
   * @returns {Promise<void>}
   * @throws {Error} If distillery has associated bottles
   */
  async deleteDistillery(id) {
    // Check for associated bottles
    const { count, error: countError } = await supabase
      .from('bottles')
      .select('*', { count: 'exact', head: true })
      .eq('distillery_id', id);

    if (countError) throw countError;

    if (count > 0) {
      throw new Error(`Cannot delete distillery. It has ${count} associated bottle${count === 1 ? '' : 's'}. Remove or reassign bottles first.`);
    }

    const { error } = await supabase
      .from('distilleries')
      .delete()
      .eq('id', id);

    if (error) throw error;
  }
};
